import { computed, signal } from '@preact/signals';
import { setProfilerEnabled, syncProfilerSignal } from './frameProfiler';

// ============================================================================
// Performance Metrics (Direct DOM - no signals to avoid re-render overhead)
// ============================================================================

let frameCount = 0;
let lastFpsUpdate = performance.now();
let lastFrameTime = performance.now();
let currentFps = 0;
let currentFrameTime = 0;
let fpsElement: HTMLDivElement | null = null;

export const getFps = (): number => currentFps;
export const getFrameTime = (): number => currentFrameTime;

// Create simple FPS overlay (always visible, no signals)
export const createFpsOverlay = (): void => {
  if (fpsElement) {
    return;
  }
  fpsElement = document.createElement('div');
  fpsElement.id = 'fps-overlay';
  fpsElement.style.cssText = `
    position: fixed;
    top: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.7);
    color: #4ade80;
    font-family: monospace;
    font-size: 12px;
    padding: 4px 8px;
    border-radius: 4px;
    z-index: 9999;
    pointer-events: none;
  `;
  document.body.appendChild(fpsElement);
};

export const removeFpsOverlay = (): void => {
  if (fpsElement) {
    fpsElement.remove();
    fpsElement = null;
  }
};

export const updatePerformanceMetrics = (): void => {
  const now = performance.now();
  currentFrameTime = now - lastFrameTime;
  lastFrameTime = now;

  frameCount++;
  const elapsed = now - lastFpsUpdate;
  if (elapsed >= 500) {
    currentFps = Math.round((frameCount * 1000) / elapsed);
    frameCount = 0;
    lastFpsUpdate = now;

    // Update DOM directly - no signal overhead
    if (fpsElement) {
      const color = currentFps >= 55 ? '#4ade80' : currentFps >= 30 ? '#facc15' : '#f87171';
      fpsElement.style.color = color;
      fpsElement.textContent = `${currentFps} FPS`;
    }

    // Sync signals for debug overlay (~2x/sec, not every frame)
    syncDebugSignals();
  }
};

// For debug overlay (only reads when shown)
export const fpsSignal = signal(0);
export const frameTimeSignal = signal(0);

// Placement debug info (for build/placement validation)
export interface PlacementDebugInfo {
  readonly tileX: number;
  readonly tileY: number;
  readonly chunkX: number;
  readonly chunkY: number;
  readonly hasChunk: boolean;
  readonly tileType: number | null;
  readonly tileWalkable: boolean;
  readonly blockedByObject: boolean;
  readonly blockingObjectType: number | null;
  readonly blockedByConstruction: boolean;
  readonly canPlaceAtTile: boolean;
  readonly canPlaceFootprint: boolean | null;
  readonly footprintReason: string | null;
}

export const placementDebug = signal<PlacementDebugInfo | null>(null);

export const setPlacementDebug = ({ info }: { info: PlacementDebugInfo | null }): void => {
  placementDebug.value = info;
};

// Call this sparingly (e.g., twice per second) to update debug panel
export const syncDebugSignals = (): void => {
  fpsSignal.value = currentFps;
  frameTimeSignal.value = currentFrameTime;
  syncProfilerSignal();
};

// ============================================================================
// Debug Flags
// ============================================================================

// Debug feature flags
export interface DebugFlags {
  readonly showChunkBorders: boolean;
  readonly showOrigin: boolean;
  readonly showTileGrid: boolean;
  readonly showPerformance: boolean;
  readonly showEntityIds: boolean;
  readonly showNetworkInfo: boolean;
  readonly showPlacement: boolean;
  readonly showFrameProfile: boolean;
}

const DEFAULT_DEBUG_FLAGS: DebugFlags = {
  showChunkBorders: false,
  showOrigin: false,
  showTileGrid: false,
  showPerformance: false,
  showEntityIds: false,
  showNetworkInfo: false,
  showPlacement: false,
  showFrameProfile: false,
};

// Main debug state
const debugEnabled = signal(false);
const debugFlags = signal<DebugFlags>(DEFAULT_DEBUG_FLAGS);

// Computed: is any debug feature active
export const isDebugActive = computed(() => debugEnabled.value);

// Get current debug flags (only active when debug mode is on)
export const getDebugFlags = (): DebugFlags => {
  if (!debugEnabled.value) {
    return DEFAULT_DEBUG_FLAGS;
  }
  return debugFlags.value;
};

// Toggle entire debug mode
export const toggleDebugMode = (): boolean => {
  debugEnabled.value = !debugEnabled.value;
  if (debugEnabled.value) {
    createFpsOverlay();
  } else {
    removeFpsOverlay();
  }
  console.log(`Debug mode: ${debugEnabled.value ? 'ON' : 'OFF'}`);
  return debugEnabled.value;
};

// Set debug mode explicitly
export const setDebugMode = ({ enabled }: { enabled: boolean }): void => {
  debugEnabled.value = enabled;
};

// Toggle a specific debug flag
export const toggleDebugFlag = <K extends keyof DebugFlags>({ flag }: { flag: K }): boolean => {
  const current = debugFlags.value[flag];
  debugFlags.value = { ...debugFlags.value, [flag]: !current };
  console.log(`Debug flag '${flag}': ${!current ? 'ON' : 'OFF'}`);

  // Enable/disable frame profiler when flag changes
  if (flag === 'showFrameProfile') {
    setProfilerEnabled({ enabled: !current });
  }

  return !current;
};

// Set a specific debug flag
export const setDebugFlag = <K extends keyof DebugFlags>({
  flag,
  value,
}: {
  flag: K;
  value: DebugFlags[K];
}): void => {
  debugFlags.value = { ...debugFlags.value, [flag]: value };
};

// Get a specific debug flag value
export const getDebugFlag = <K extends keyof DebugFlags>({ flag }: { flag: K }): boolean => {
  if (!debugEnabled.value) {
    return false;
  }
  return debugFlags.value[flag];
};

// Reset all debug flags to defaults
export const resetDebugFlags = (): void => {
  debugFlags.value = DEFAULT_DEBUG_FLAGS;
  setProfilerEnabled({ enabled: false });
};

// Enable all debug flags at once
export const enableAllDebugFlags = (): void => {
  debugFlags.value = {
    showChunkBorders: true,
    showOrigin: true,
    showTileGrid: true,
    showPerformance: true,
    showEntityIds: true,
    showNetworkInfo: true,
    showPlacement: true,
    showFrameProfile: true,
  };
  setProfilerEnabled({ enabled: true });
};

// Keyboard shortcut handler for debug toggles
// Call this from input handling: handleDebugInput({ key })
export const handleDebugInput = ({ key }: { key: string }): boolean => {
  // F3 toggles debug mode
  if (key === 'F3') {
    toggleDebugMode();
    return true;
  }

  // Only process debug shortcuts if debug mode is on
  if (!debugEnabled.value) {
    return false;
  }

  switch (key) {
    case '1':
      toggleDebugFlag({ flag: 'showChunkBorders' });
      return true;
    case '2':
      toggleDebugFlag({ flag: 'showOrigin' });
      return true;
    case '3':
      toggleDebugFlag({ flag: 'showTileGrid' });
      return true;
    case '4':
      toggleDebugFlag({ flag: 'showPerformance' });
      return true;
    case '5':
      toggleDebugFlag({ flag: 'showEntityIds' });
      return true;
    case '6':
      toggleDebugFlag({ flag: 'showNetworkInfo' });
      return true;
    case '7':
      toggleDebugFlag({ flag: 'showPlacement' });
      return true;
    case '8':
      toggleDebugFlag({ flag: 'showFrameProfile' });
      return true;
    case '0':
      resetDebugFlags();
      console.log('Debug flags reset');
      return true;
    case '9':
      enableAllDebugFlags();
      console.log('All debug flags enabled');
      return true;
    default:
      return false;
  }
};

// Export for reactive UI components
export { debugEnabled, debugFlags };
